import { ArrowUpRight, Check, CircleAlert, Copy, FileBox, Globe, Link, MapPin, Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { normalizeSourceUrl, sourceHost, type SchematicRef } from "@mcp/plan";
import { controller } from "../../app/controller.ts";
import { fileSize, stacks, timeAgo, type SchematicEntry } from "../../lib/schematics.ts";
import { useApp } from "../../state/store.ts";
import { blockLabel, materialsAsText, swatch } from "./format.ts";

export interface Missing { ref: SchematicRef; name: string; sourceUrl?: string; count: number }

const SHOWN = 9;

function Swatch({ id }: { id: string }) {
  const s = useMemo(() => swatch(id), [id]);
  return s.img
    ? <img className="sv-swatch" src={s.img} alt="" width={18} height={18} />
    : <span className="sv-swatch" style={{ background: s.colour }} aria-hidden="true" />;
}

/** Materials with swatches, counts and stacks; the long tail folds into one row until it's opened. */
function Materials({ list, name, sourceUrl }: { list: [string, number][]; name: string; sourceUrl?: string }) {
  const [all, setAll] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setAll(false); setCopied(false); }, [list]);
  const head = all || list.length <= SHOWN + 1 ? list : list.slice(0, SHOWN);
  const tail = head.length < list.length ? list.slice(SHOWN) : [];
  const copy = async () => {
    try { await navigator.clipboard.writeText(materialsAsText(name, list, sourceUrl)); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* clipboard refused */ }
  };
  if (!list.length) return null;
  return (
    <section className="sv-mats">
      <div className="sv-section-head">
        <span className="overline">Materials</span>
        <button type="button" className="btn btn-sm sv-copy" onClick={copy}>{copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}{copied ? "Copied" : "Copy list"}</button>
      </div>
      <div role="table" aria-label="Materials" className="sv-mat-table">
        {head.map(([id, n]) => (
          <div role="row" className="sv-mat" key={id} title={id}>
            <Swatch id={id} />
            <span role="cell" className="sv-mat-name">{blockLabel(id)}</span>
            <span role="cell" className="sv-mat-n tabular">{n.toLocaleString("en-US")}</span>
            <span role="cell" className="sv-mat-st mono">{stacks(n)}</span>
          </div>
        ))}
      </div>
      {tail.length > 0 && (
        <button type="button" className="sv-mat sv-mat-more" onClick={() => setAll(true)} title="Show every material" aria-label={`Show ${tail.length} more materials`}>
          <span className="sv-swatch split" aria-hidden="true" style={{ background: `linear-gradient(135deg, ${swatch(tail[0][0]).colour} 0 50%, ${swatch(tail[1]?.[0] ?? tail[0][0]).colour} 50% 100%)` }} />
          <span className="sv-mat-name">{tail.slice(0, 3).map(([id]) => blockLabel(id).toLowerCase()).join(", ").replace(/^./, (c) => c.toUpperCase())}{tail.length > 3 ? ` and ${tail.length - 3} more` : ""}</span>
          <span className="sv-mat-n tabular">{tail.reduce((a, [, n]) => a + n, 0).toLocaleString("en-US")}</span>
          <span className="sv-mat-st" />
        </button>
      )}
      {all && list.length > SHOWN + 1 && <button type="button" className="btn btn-sm btn-ghost sv-less" onClick={() => setAll(false)}>Show fewer</button>}
    </section>
  );
}

function Stat({ label, value, quiet }: { label: string; value: string; quiet?: boolean }) {
  return (
    <div className="sv-stat">
      <div className="sv-stat-label">{label}</div>
      <div className={`sv-stat-value tabular${quiet ? " quiet" : ""}${value.length > 8 ? " long" : ""}`} title={value}>{value}</div>
    </div>
  );
}

function SourceLink({ url, onAdd }: { url?: string; onAdd?: () => void }) {
  if (url) {
    return <a className="sv-source" href={url} target="_blank" rel="noopener noreferrer" title={url}><Globe size={14} aria-hidden="true" />{sourceHost(url)}<ArrowUpRight size={14} aria-hidden="true" /></a>;
  }
  return onAdd ? <button type="button" className="sv-source add" onClick={onAdd}><Link size={14} aria-hidden="true" />Add the link to the original post</button> : <span className="sv-source none caption">No link to the original post</span>;
}

/** Details rail of a schematic in my library. */
export function FileDetails({ e, placed, world, turns, mirror }: { e: SchematicEntry; placed: number; world: string; turns: number; mirror: boolean }) {
  const [sure, setSure] = useState(false);
  useEffect(() => setSure(false), [e.hash]);
  const edit = () => useApp.getState().set({ schemEdit: e.hash });
  return (
    <>
      <div className="sv-details-body">
        <div className="sv-title-row">
          <h1 className="title-md sv-name" title={e.name}>{e.name}</h1>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={edit} title="Name and link" aria-label="Edit name and link"><Pencil size={14} aria-hidden="true" /></button>
        </div>
        <SourceLink url={e.sourceUrl} onAdd={edit} />
        <p className="sv-meta caption">
          <span className="mono sv-file" title={e.fileName}>{e.fileName}</span> · {fileSize(e.bytes)} · imported {timeAgo(e.addedAt)}{e.author ? ` · by ${e.author}` : ""}
        </p>
        <div className="sv-stats">
          <Stat label="Size" value={`${e.size[0]}×${e.size[1]}×${e.size[2]}`} />
          <Stat label="Blocks" value={e.blocks.toLocaleString("en-US")} />
          <Stat label="In this world" value={placed ? `${placed} placed` : "Not yet"} quiet={!placed} />
        </div>
        {e.description && <p className="sv-desc caption">{e.description}</p>}
        <Materials list={e.materials} name={e.name} sourceUrl={e.sourceUrl} />
      </div>
      <div className="sv-details-foot">
        <button type="button" className="btn btn-lg btn-primary btn-block sv-place" onClick={() => controller.startSchematicPlacing(e.hash, { turns, mirror })}>
          <MapPin size={16} aria-hidden="true" />Place in {world}
        </button>
        <span className="sv-foot-hint">Click on the map to place it · Shift+click places more</span>
        {sure ? (
          <div className="sv-confirm" role="alert">
            <span className="caption">Remove it from this computer? Buildings already placed stay on the map.</span>
            <div className="sv-foot-row">
              <button type="button" className="btn btn-outline btn-danger sv-remove-yes" onClick={() => void controller.deleteSchematic(e.hash)}>Remove</button>
              <button type="button" className="btn btn-outline" onClick={() => setSure(false)}>Keep</button>
            </div>
          </div>
        ) : (
          <div className="sv-foot-row">
            {e.sourceUrl
              ? <a className="btn btn-outline sv-grow" href={e.sourceUrl} target="_blank" rel="noopener noreferrer">Open original post<ArrowUpRight size={14} aria-hidden="true" /></a>
              : <button type="button" className="btn btn-outline sv-grow" onClick={edit}><Link size={14} aria-hidden="true" />Add the link</button>}
            <button type="button" className="btn btn-outline btn-icon btn-danger sv-remove" onClick={() => setSure(true)} aria-label="Remove from this computer" title="Remove from this computer"><Trash2 size={16} aria-hidden="true" /></button>
          </div>
        )}
      </div>
    </>
  );
}

/** Details rail of a schematic used in this world whose file isn't here (someone else placed it). */
export function MissingDetails({ m, world, onImport }: { m: Missing; world: string; onImport: () => void }) {
  const r = m.ref;
  return (
    <>
      <div className="sv-details-body">
        <span className="chip chip-amber sv-missing-chip"><CircleAlert size={12} aria-hidden="true" />Not on this computer</span>
        <h1 className="title-md sv-name" title={m.name}>{m.name}</h1>
        <SourceLink url={m.sourceUrl} />
        <p className="sv-meta caption">
          <span className="mono sv-file" title={r.file}>{r.file}</span>{r.author ? ` · by ${r.author}` : ""}
        </p>
        <div className="sv-stats">
          <Stat label="Size" value={`${r.size[0]}×${r.size[1]}×${r.size[2]}`} />
          <Stat label="Blocks" value={(r.blocks ?? 0).toLocaleString("en-US")} />
          <Stat label="In this world" value={`${m.count} placed`} />
        </div>
        <p className="sv-desc caption">
          It's placed in {world}, but the file isn't on this computer. Files stay with the people who imported them: get it from
          the original post, then import it here and the buildings show up by themselves.
        </p>
        <Materials list={r.materials ?? []} name={m.name} sourceUrl={m.sourceUrl} />
      </div>
      <div className="sv-details-foot">
        {m.sourceUrl
          ? <a className="btn btn-lg btn-primary btn-block sv-get" href={m.sourceUrl} target="_blank" rel="noopener noreferrer">Get it from {sourceHost(m.sourceUrl)}<ArrowUpRight size={16} aria-hidden="true" /></a>
          : <span className="sv-foot-hint">There's no link to the original post · ask the person who placed it for the file</span>}
        <button type="button" className={`btn ${m.sourceUrl ? "btn-outline" : "btn-lg btn-primary"} btn-block`} onClick={onImport}><FileBox size={16} aria-hidden="true" />Import the file</button>
      </div>
    </>
  );
}

/** Name + link to the original post: right after importing, and from the pencil. */
export function ImportForm({ e, world, fresh }: { e: SchematicEntry; world: string; fresh: boolean }) {
  const [name, setName] = useState(e.name);
  const [link, setLink] = useState(e.sourceUrl ?? "");
  useEffect(() => { setName(e.name); setLink(e.sourceUrl ?? ""); }, [e.hash]);
  const bad = !!link.trim() && !normalizeSourceUrl(link);
  const save = async () => {
    if (bad) return false;
    await controller.editSchematic(e.hash, { name, sourceUrl: link });
    return true;
  };
  return (
    <form
      className="sv-form"
      onSubmit={async (ev) => { ev.preventDefault(); if (await save()) controller.startSchematicPlacing(e.hash); }}
      onKeyDown={(ev) => { if (ev.key === "Escape") { ev.stopPropagation(); useApp.getState().set({ schemEdit: null }); } }}
    >
      <div className="sv-details-body">
        <span className="overline">{fresh ? "Imported" : "Name and link"}</span>
        <h1 className="title-sm sv-form-title">{fresh ? "Where is it from?" : "Edit name and link"}</h1>
        <p className="caption sv-form-lead">Keep the link to the original post: everyone planning with you sees it and gets the file from its creator.</p>
        <div className="sv-file-card">
          <FileBox size={16} aria-hidden="true" />
          <span className="mono sv-file" title={e.fileName}>{e.fileName}</span>
          <span className="caption">{fileSize(e.bytes)} · {e.size[0]}×{e.size[1]}×{e.size[2]}</span>
        </div>
        <label className="field-label" htmlFor="sv-name">Name</label>
        <label className="field-box"><input id="sv-name" value={name} maxLength={80} onChange={(ev) => setName(ev.target.value)} /></label>
        <label className="field-label" htmlFor="sv-link">Link to the original post</label>
        <label className={`field-box${bad ? " invalid" : ""}`}>
          <Link size={16} aria-hidden="true" />
          <input id="sv-link" type="url" value={link} placeholder="https://www.planetminecraft.com/project/…" autoFocus onChange={(ev) => setLink(ev.target.value)} aria-invalid={bad} />
        </label>
        {bad
          ? <span className="field-error warn" role="alert">Only web links (https://…)</span>
          : <span className="field-hint">Shown on the building, so everyone can find the creator and download the file.</span>}
      </div>
      <div className="sv-details-foot">
        <button type="submit" className="btn btn-lg btn-primary btn-block" disabled={bad}><MapPin size={16} aria-hidden="true" />Place in {world}</button>
        <div className="sv-foot-row">
          <button type="button" className="btn btn-outline sv-grow sv-save" disabled={bad} onClick={async () => { if (await save()) useApp.getState().set({ schemEdit: null }); }}>Save</button>
          {!fresh && <button type="button" className="btn btn-ghost" onClick={() => useApp.getState().set({ schemEdit: null })}>Cancel</button>}
        </div>
      </div>
    </form>
  );
}
